import {ListItem, ListView} from '@github-ui/list-view'
import {ProjectTemplateIcon} from '@primer/octicons-react'
import {Box, Text} from '@primer/react'

import type {CustomTemplate} from '../../api/common-contracts'
import {sanitizeTextInputHtmlString} from '../../helpers/sanitize'
import {Link} from '../../router'
import {useTemplateLink} from './hooks/use-template-link'

function TemplateListItem({template}: {template: CustomTemplate}) {
  const to = useTemplateLink({type: 'custom', template})

  return (
    <ListItem
      sx={{
        // Adjusted grid spacing to make the list denser
        columnGap: 2,
        rowGap: 0,
        pl: 2,
        // We are overwriting the hover styling so that the background color on hover
        // doesn't overlap with the rounded border
        '&:hover': {backgroundColor: 'transparent'},
      }}
      title={
        <ListItem.Title
          value={sanitizeTextInputHtmlString(template.projectTitle)}
          href={'#' /* this will be replaced by `to` */}
          linkProps={{as: Link, to}}
          headerSx={{fontSize: 1, mt: 1}}
        />
      }
    >
      <ListItem.LeadingVisual icon={ProjectTemplateIcon} color="fg.muted" />
      <ListItem.MainContent>
        {template.projectShortDescription && (
          <ListItem.Description>{template.projectShortDescription}</ListItem.Description>
        )}
      </ListItem.MainContent>
    </ListItem>
  )
}

export function TemplateList({
  title,
  templates,
  metadata,
}: {
  /** accessible name for the list view */
  title: string
  templates: Array<CustomTemplate>
  metadata?: React.ReactNode
}) {
  return (
    <Box sx={{borderWidth: 1, borderStyle: 'solid', borderRadius: 2, borderColor: 'border.default'}}>
      <ListView
        title={title}
        metadata={
          <ListView.Metadata
            title={
              <Text
                sx={{
                  fontWeight: 'bold',
                  color: 'fg.muted',
                }}
              >
                {title}
              </Text>
            }
            sx={{backgroundColor: 'canvas.subtle', pl: '12px', fontSize: 0, py: 2, borderBottomColor: 'border.default'}}
          >
            {metadata}
          </ListView.Metadata>
        }
      >
        {templates.map(template => (
          <TemplateListItem key={template.projectNumber} template={template} />
        ))}
      </ListView>
    </Box>
  )
}

try{ TemplateListItem.displayName ||= 'TemplateListItem' } catch {}
try{ TemplateList.displayName ||= 'TemplateList' } catch {}